/**
 * Microservice backends:
 * - IAM / user-management (`BACKEND_URL`) — login, account, users, roles, permissions
 * - CRM (`CRM_BACKEND_URL`) — cases, agent panel, reports, qstat, chat websocket, …
 *
 * Local dev: `USE_DEV_PROXY=1` — browser uses same-origin paths; webpack proxies each prefix.
 */
import { BUILD_ENV, IS_PRODUCTION_BUILD } from './build-env';
/** User-management / IAM gateway (authenticate, account, admin users, roles, permissions). */
export const DEFAULT_IAM_BACKEND_URL = 'https://backend-sp17.finlevia.af';
/** Call-center CRM APIs (cases, campaigns, reports, profile pause, chat REST, …). */
export const DEFAULT_CRM_BACKEND_URL = 'https://crm-backend.finlevia.af';
function envFlagIsTrue(value) {
    return value === '1' || value.toLowerCase() === 'true';
}
/** Normalize host with optional scheme (`backend.example.af` → `https://…`). */
export function normalizeBackendUrl(raw) {
    const trimmed = raw.trim().replace(/\/$/, '');
    if (!trimmed || trimmed === 'relative' || trimmed === '/') {
        return '';
    }
    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }
    return `https://${trimmed}`;
}
export function configuredIamBackendUrlRaw() {
    return BUILD_ENV.BACKEND_URL;
}
export function configuredCrmBackendUrlRaw() {
    return BUILD_ENV.CRM_BACKEND_URL;
}
function isWebpackServe() {
    return BUILD_ENV.WEBPACK_SERVE === '1' || BUILD_ENV.WEBPACK_SERVE.toLowerCase() === 'true';
}
/**
 * When true, axios uses relative `/api`, `/user-management`, … and webpack proxies by path prefix.
 */
export function usesDevProxy() {
    const flag = BUILD_ENV.USE_DEV_PROXY;
    if (envFlagIsTrue(flag)) {
        return true;
    }
    if (flag === '0' || flag.toLowerCase() === 'false') {
        return false;
    }
    const hasExplicitOrigin = Boolean(normalizeBackendUrl(configuredIamBackendUrlRaw())) ||
        Boolean(normalizeBackendUrl(configuredCrmBackendUrlRaw()));
    if (hasExplicitOrigin) {
        return false;
    }
    return !IS_PRODUCTION_BUILD || isWebpackServe();
}
/** Absolute IAM origin for browser calls, or empty when using same-origin dev proxy. */
export function getIamBackendOrigin() {
    if (usesDevProxy()) {
        return '';
    }
    return normalizeBackendUrl(configuredIamBackendUrlRaw()) || DEFAULT_IAM_BACKEND_URL;
}
/** Absolute CRM origin for browser calls, or empty when using same-origin dev proxy. */
export function getCrmBackendOrigin() {
    if (usesDevProxy()) {
        return '';
    }
    return normalizeBackendUrl(configuredCrmBackendUrlRaw()) || DEFAULT_CRM_BACKEND_URL;
}
/** Webpack dev-server proxy target for IAM paths (`/user-management`, `/user`, …). */
export function resolveIamBackendProxyTarget() {
    return normalizeBackendUrl(configuredIamBackendUrlRaw()) || DEFAULT_IAM_BACKEND_URL;
}
/** Webpack dev-server proxy target for CRM paths (`/api`, `/websocket`, `/cms`, …). */
export function resolveCrmBackendProxyTarget() {
    return normalizeBackendUrl(configuredCrmBackendUrlRaw()) || DEFAULT_CRM_BACKEND_URL;
}
/** Build an IAM backend URL (`/user-management/...`, `/audit-log/...`). */
export function iamBackendUrl(pathFromSlash) {
    const rel = pathFromSlash.startsWith('/') ? pathFromSlash : `/${pathFromSlash}`;
    const origin = getIamBackendOrigin();
    return origin ? `${origin}${rel}` : rel;
}
/** Build a CRM backend URL (`/api/...`, `/cms/...`). */
export function crmBackendUrl(pathFromSlash) {
    const rel = pathFromSlash.startsWith('/') ? pathFromSlash : `/${pathFromSlash}`;
    const origin = getCrmBackendOrigin();
    return origin ? `${origin}${rel}` : rel;
}
export function isIamApiPath(path) {
    return (path.startsWith('/user-management/') ||
        path.startsWith('/user/') ||
        path.startsWith('/audit-log/') ||
        isFinleviaCallCenterIamPath(path));
}
/** Agent-panel customer 360 — hosted on IAM gateway (`BACKEND_URL`), not CRM. */
export function isFinleviaCallCenterIamPath(path) {
    const base = path.split('?')[0] ?? path;
    return (base.startsWith('/api/v1/call-center/customers/') ||
        base.startsWith('/api/v1/call-center/transactions/partner/') ||
        base.startsWith('/cms/api/cards'));
}
export function isCrmApiPath(path) {
    return (path.startsWith('/api/') ||
        path === '/api' ||
        path.startsWith('/cms/') ||
        path.startsWith('/websocket/'));
}
/** Pick IAM vs CRM origin for an absolute browser URL (empty when dev proxy uses relative paths). */
export function resolveApiOriginForPath(path) {
    if (usesDevProxy()) {
        return '';
    }
    if (isIamApiPath(path)) {
        return getIamBackendOrigin();
    }
    if (isCrmApiPath(path)) {
        return getCrmBackendOrigin();
    }
    return '';
}
